import React, { useState, useEffect, useCallback } from "react";
import {
  makeStyles,
  Paper,
  TextField,
  Button,
  IconButton,
  Checkbox,
  Chip,
  Typography,
  CircularProgress,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Input,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";
import api from "../../services/api";

const PRIORITY_LABELS = { high: "Alta", medium: "Média", low: "Baixa" };
const PRIORITY_COLORS = { high: "#e53935", medium: "#fb8c00", low: "#1e88e5" };

const formatDate = date => {
  if (!date) return null;
  const d = new Date(date);
  return `${d.getUTCDate().toString().padStart(2, "0")}/${(d.getUTCMonth() + 1)
    .toString()
    .padStart(2, "0")}/${d.getUTCFullYear()}`;
};

const toInputDate = date => {
  if (!date) return "";
  const d = new Date(date);
  return d.toISOString().split("T")[0];
};

const isOverdue = task => {
  if (task.completed || !task.dueDate) return false;
  const due = new Date(task.dueDate);
  const today = new Date();
  const dueDay = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate());
  const todayDay = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return dueDay < todayDay;
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 760,
    margin: "0 auto",
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
  },
  formRow: {
    display: "flex",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: { flexDirection: "column" },
  },
  filters: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  filterChip: { cursor: "pointer" },
  taskCard: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[1],
    borderLeft: "4px solid transparent",
  },
  taskCardOverdue: {
    borderLeftColor: "#e53935",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(229,57,53,0.08)"
        : "rgba(229,57,53,0.04)",
  },
  taskCardCompleted: {
    opacity: 0.6,
  },
  taskBody: {
    flex: 1,
    marginLeft: theme.spacing(1),
  },
  taskText: {
    fontSize: "0.95rem",
    wordBreak: "break-word",
  },
  taskTextCompleted: {
    textDecoration: "line-through",
    color: theme.palette.text.disabled,
  },
  taskMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    flexWrap: "wrap",
  },
  dueDateText: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
  dueDateOverdue: {
    color: "#e53935",
    fontWeight: 600,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  checkboxRoot: {
    color: theme.palette.success.main,
    padding: 4,
    "&$checked": { color: theme.palette.success.main },
  },
  checked: {},
  inlineInput: {
    fontSize: "0.95rem",
    width: "100%",
  },
  summary: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
}));

const FILTERS = [
  { key: "all", label: "Todas" },
  { key: "pending", label: "Pendentes" },
  { key: "done", label: "Concluídas" },
  { key: "overdue", label: "Atrasadas" },
];

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };

const ToDoList = () => {
  const classes = useStyles();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  const [newText, setNewText] = useState("");
  const [newPriority, setNewPriority] = useState("medium");
  const [newDueDate, setNewDueDate] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchTasks = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks");
      setTasks(data);
    } catch {
      toast.error("Erro ao carregar tarefas.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleAdd = async () => {
    if (!newText.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/tasks", {
        text: newText.trim(),
        priority: newPriority,
        dueDate: newDueDate || null,
      });
      setTasks(prev => [data, ...prev]);
      setNewText("");
      setNewPriority("medium");
      setNewDueDate("");
    } catch {
      toast.error("Erro ao criar tarefa.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async task => {
    try {
      const { data } = await api.put(`/tasks/${task.id}`, {
        completed: !task.completed,
      });
      setTasks(prev => prev.map(t => (t.id === task.id ? data : t)));
    } catch {
      toast.error("Erro ao atualizar tarefa.");
    }
  };

  const handleDelete = async id => {
    try {
      await api.delete(`/tasks/${id}`);
      setTasks(prev => prev.filter(t => t.id !== id));
    } catch {
      toast.error("Erro ao excluir tarefa.");
    }
  };

  const handleEditStart = task => {
    setEditingId(task.id);
    setEditingText(task.text);
  };

  const handleEditSave = async task => {
    if (!editingText.trim()) return;
    try {
      const { data } = await api.put(`/tasks/${task.id}`, {
        text: editingText.trim(),
      });
      setTasks(prev => prev.map(t => (t.id === task.id ? data : t)));
    } catch {
      toast.error("Erro ao salvar tarefa.");
    } finally {
      setEditingId(null);
    }
  };

  const filteredTasks = tasks
    .filter(t => {
      if (filter === "pending") return !t.completed;
      if (filter === "done") return t.completed;
      if (filter === "overdue") return isOverdue(t);
      return true;
    })
    .sort((a, b) => {
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      const pa = PRIORITY_ORDER[a.priority] ?? 1;
      const pb = PRIORITY_ORDER[b.priority] ?? 1;
      return pa - pb;
    });

  const pendingCount = tasks.filter(t => !t.completed).length;
  const overdueCount = tasks.filter(isOverdue).length;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5" className={classes.title}>
          Minhas Tarefas
        </Typography>
        <Typography className={classes.summary}>
          {pendingCount} pendente{pendingCount !== 1 ? "s" : ""}
          {overdueCount > 0 && (
            <span style={{ color: "#e53935", marginLeft: 8 }}>
              · {overdueCount} atrasada{overdueCount !== 1 ? "s" : ""}
            </span>
          )}
        </Typography>
      </div>

      {/* Formulário de nova tarefa */}
      <Paper className={classes.form}>
        <TextField
          label="Nova tarefa"
          variant="outlined"
          size="small"
          fullWidth
          value={newText}
          onChange={e => setNewText(e.target.value)}
          onKeyPress={e => e.key === "Enter" && handleAdd()}
        />
        <div className={classes.formRow}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
            <InputLabel>Prioridade</InputLabel>
            <Select
              value={newPriority}
              onChange={e => setNewPriority(e.target.value)}
              label="Prioridade"
            >
              <MenuItem value="high">Alta</MenuItem>
              <MenuItem value="medium">Média</MenuItem>
              <MenuItem value="low">Baixa</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Prazo"
            type="date"
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={newDueDate}
            onChange={e => setNewDueDate(e.target.value)}
            style={{ flex: 1 }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleAdd}
            disabled={saving || !newText.trim()}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            style={{ whiteSpace: "nowrap" }}
          >
            Adicionar
          </Button>
        </div>
      </Paper>

      {/* Filtros */}
      <div className={classes.filters}>
        {FILTERS.map(f => (
          <Chip
            key={f.key}
            label={f.key === "overdue" && overdueCount > 0 ? `${f.label} (${overdueCount})` : f.label}
            onClick={() => setFilter(f.key)}
            color={filter === f.key ? "primary" : "default"}
            variant={filter === f.key ? "default" : "outlined"}
            className={classes.filterChip}
            size="small"
          />
        ))}
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 32 }}>
          <CircularProgress />
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className={classes.emptyState}>
          <Typography>Nenhuma tarefa encontrada.</Typography>
        </div>
      ) : (
        filteredTasks.map(task => (
          <Paper
            key={task.id}
            className={`${classes.taskCard} ${isOverdue(task) ? classes.taskCardOverdue : ""} ${task.completed ? classes.taskCardCompleted : ""}`}
          >
            <Checkbox
              edge="start"
              checked={task.completed}
              onChange={() => handleToggle(task)}
              classes={{ root: classes.checkboxRoot, checked: classes.checked }}
              size="small"
            />

            <div className={classes.taskBody}>
              {editingId === task.id ? (
                <Input
                  className={classes.inlineInput}
                  value={editingText}
                  onChange={e => setEditingText(e.target.value)}
                  onBlur={() => handleEditSave(task)}
                  onKeyPress={e => e.key === "Enter" && handleEditSave(task)}
                  autoFocus
                  fullWidth
                />
              ) : (
                <div className={`${classes.taskText} ${task.completed ? classes.taskTextCompleted : ""}`}>
                  {task.text}
                </div>
              )}

              <div className={classes.taskMeta}>
                <Chip
                  label={PRIORITY_LABELS[task.priority] || task.priority}
                  size="small"
                  style={{
                    backgroundColor: PRIORITY_COLORS[task.priority],
                    color: "#fff",
                    height: 20,
                    fontSize: "0.7rem",
                  }}
                />
                {task.dueDate && (
                  <span
                    className={`${classes.dueDateText} ${isOverdue(task) ? classes.dueDateOverdue : ""}`}
                  >
                    {isOverdue(task) ? "⚠ Atrasada · " : "Prazo: "}
                    {formatDate(task.dueDate)}
                  </span>
                )}
              </div>
            </div>

            <div className={classes.actions}>
              {!task.completed && editingId !== task.id && (
                <Tooltip title="Editar">
                  <IconButton size="small" onClick={() => handleEditStart(task)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Excluir">
                <IconButton size="small" onClick={() => handleDelete(task.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          </Paper>
        ))
      )}
    </div>
  );
};

export default ToDoList;
